import type { ColorBin, MetricStatistics } from "../types";

// Standard 5-step Red-to-Green & Green-to-Red Choropleth Palettes
const RED_TO_GREEN_PALETTE: readonly string[] = [
  "#ef4444", // Red (Bottom 20%)
  "#f97316", // Orange (20% - 40%)
  "#eab308", // Yellow (40% - 60% Middle / Near Median)
  "#84cc16", // Lime Green (60% - 80%)
  "#22c55e", // Green (Top 20%)
];

const GREEN_TO_RED_PALETTE: readonly string[] = [
  "#22c55e", // Green (Bottom 20%)
  "#84cc16", // Lime Green (20% - 40%)
  "#eab308", // Yellow (40% - 60% Middle / Near Median)
  "#f97316", // Orange (60% - 80%)
  "#ef4444", // Red (Top 20%)
];

const PALETTES_BY_METRIC: Record<string, readonly string[]> = {
  // Higher is Better -> RED_TO_GREEN (Low values Red, High values Green)
  "median-annual-pay": RED_TO_GREEN_PALETTE,
  "mean-annual-pay": RED_TO_GREEN_PALETTE,
  "weekly-pay": RED_TO_GREEN_PALETTE,
  "employment-rate": RED_TO_GREEN_PALETTE,
  "gdhi-per-head": RED_TO_GREEN_PALETTE,
  "gva-per-head": RED_TO_GREEN_PALETTE,
  "life-expectancy": RED_TO_GREEN_PALETTE,
  "nvq4-qualifications-rate": RED_TO_GREEN_PALETTE,
  "ev-charger-density": RED_TO_GREEN_PALETTE,
  "composite-index": RED_TO_GREEN_PALETTE,
  "gdp-per-head": RED_TO_GREEN_PALETTE,
  "business-demography": RED_TO_GREEN_PALETTE,
  "net-additions-to-the-housing-stock": RED_TO_GREEN_PALETTE,
  "gigabit-capable-broadband": RED_TO_GREEN_PALETTE,
  "network-coverage-5g": RED_TO_GREEN_PALETTE,
  "personal-wellbeing": RED_TO_GREEN_PALETTE,
  "first-time-buyer-mortgage-sales": RED_TO_GREEN_PALETTE,
  "gcse-attainment": RED_TO_GREEN_PALETTE,
  "pupils-at-expected-standard": RED_TO_GREEN_PALETTE,
  "apprenticeship-starts": RED_TO_GREEN_PALETTE,
  "fe-learner-achievements": RED_TO_GREEN_PALETTE,
  "fe-participation": RED_TO_GREEN_PALETTE,
  "early-years-language": RED_TO_GREEN_PALETTE,
  "coverage-4g": RED_TO_GREEN_PALETTE,
  "population-density": RED_TO_GREEN_PALETTE,

  // Lower is Better / Cost Burden / Negative Indicator -> GREEN_TO_RED (Low values Green, High values Red)
  "claimant-count-rate": GREEN_TO_RED_PALETTE,
  "housing-affordability-ratio": GREEN_TO_RED_PALETTE,
  "average-house-price": GREEN_TO_RED_PALETTE,
  "co2-emissions-per-capita": GREEN_TO_RED_PALETTE,
  population: GREEN_TO_RED_PALETTE,
  "modelled-unemployment": GREEN_TO_RED_PALETTE,
  "fuel-poverty": GREEN_TO_RED_PALETTE,
  "children-in-relative-poverty": GREEN_TO_RED_PALETTE,
  "adult-obesity-prevalence": GREEN_TO_RED_PALETTE,
  "car-commute-time": GREEN_TO_RED_PALETTE,
  "no-qualifications": GREEN_TO_RED_PALETTE,
  "persistent-absences-all": GREEN_TO_RED_PALETTE,
  "persistent-absences-fsm": GREEN_TO_RED_PALETTE,
  "child-obesity": GREEN_TO_RED_PALETTE,
  "adult-smokers": GREEN_TO_RED_PALETTE,
  "cardiovascular-mortality": GREEN_TO_RED_PALETTE,
  "electricity-consumption": GREEN_TO_RED_PALETTE,
  "gas-consumption": GREEN_TO_RED_PALETTE,
  "premises-below-30mbps": GREEN_TO_RED_PALETTE,
};

const PALETTES_BY_UNIT: Record<string, readonly string[]> = {
  "currency-gbp": RED_TO_GREEN_PALETTE,
  percentage: RED_TO_GREEN_PALETTE,
  ratio: GREEN_TO_RED_PALETTE,
  rate: GREEN_TO_RED_PALETTE,
  count: GREEN_TO_RED_PALETTE,
  score: RED_TO_GREEN_PALETTE,
};

// Domain-Specific Cutoffs for UK Housing & Financial Policy Metrics
const METRIC_CUSTOM_CUTOFFS: Record<string, readonly number[]> = {
  // Traditional mortgage lending limit is 4.5x - 5.0x annual earnings
  "housing-affordability-ratio": [4.5, 5, 6, 8],
};

const DEFAULT_CHOROPLETH_PALETTE: readonly string[] = RED_TO_GREEN_PALETTE;

const gbpFormatterInt = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const gbpFormatterDec = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const numFormatterInt = new Intl.NumberFormat("en-GB", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const numFormatterDec = new Intl.NumberFormat("en-GB", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatMetricValue(value: number | null, unit: string): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "Suppressed / Unavailable";
  }

  const absVal = Math.abs(value);

  if (unit === "currency-gbp") {
    const useDecimals = absVal < 1000 && value % 1 !== 0;
    return useDecimals ? gbpFormatterDec.format(value) : gbpFormatterInt.format(value);
  }

  if (unit === "percentage") {
    return `${value.toFixed(2)}%`;
  }

  if (unit === "ratio") {
    return `${value.toFixed(2)}x`;
  }

  if (unit === "score") {
    return `${value.toFixed(1)} / 100`;
  }

  if (unit === "score-10") {
    return `${value.toFixed(1)} / 10`;
  }

  // Value-referenced units: the unit string is the literal display suffix.
  const useDecimals = absVal < 1000 && value % 1 !== 0;
  const number = useDecimals ? numFormatterDec.format(value) : numFormatterInt.format(value);
  return `${number} ${unit}`;
}

export function computeStatistics(values: number[]): MetricStatistics | null {
  if (values.length === 0) return null;

  const sorted = [...values].toSorted((a, b) => a - b);
  const count = sorted.length;
  const min = sorted[0];
  const max = sorted[count - 1];

  const sum = sorted.reduce((acc, curr) => acc + curr, 0);
  const mean = sum / count;

  const median = count % 2 === 0 ? (sorted[count / 2 - 1] + sorted[count / 2]) / 2 : sorted[Math.floor(count / 2)];

  const variance = sorted.reduce((acc, curr) => acc + (curr - mean) ** 2, 0) / count;
  const stdDev = Math.sqrt(variance);

  return { min, max, mean, median, p50: median, stdDev, count };
}

/**
 * Computes 5 choropleth color bins.
 * Uses equal-count quantile (percentile) binning so the full palette is always
 * represented and wide-range metrics with outliers (e.g. house prices with
 * London several σ above the rest) don't squash every other area into one
 * band. If the percentiles collapse (common for tight, high-coverage %
 * metrics), it falls back to equal-width bands across [min, max] so the map
 * never becomes a single colour.
 * 1. Domain-Specific Cutoffs if configured for metric (e.g. Housing Affordability 5.5x, 7.0x, 8.5x, 11.0x)
 * 2. Quantile (Percentile) Binning for general distributions
 */
export function computeColorBins(
  input: number[] | MetricStatistics,
  paletteOrMetric?: readonly string[] | string,
  unit?: string,
): ColorBin[] {
  if (!input) return [];
  const rawValues = Array.isArray(input) ? input : null;
  const stats = Array.isArray(input) ? computeStatistics(input) : input;
  if (!stats) return [];

  let palette: readonly string[] = DEFAULT_CHOROPLETH_PALETTE;
  if (Array.isArray(paletteOrMetric)) {
    palette = paletteOrMetric;
  } else if (typeof paletteOrMetric === "string") {
    if (PALETTES_BY_METRIC[paletteOrMetric]) {
      palette = PALETTES_BY_METRIC[paletteOrMetric];
    } else if (PALETTES_BY_UNIT[paletteOrMetric]) {
      palette = PALETTES_BY_UNIT[paletteOrMetric];
    }
  }

  const { min, max } = stats;

  if (min === max) {
    return [
      {
        min,
        max,
        color: palette[0],
        label: unit ? formatMetricValue(min, unit) : `${min}`,
      },
    ];
  }

  // 1. Explicit Domain Policy Cutoffs if configured for this metric
  if (typeof paletteOrMetric === "string" && METRIC_CUSTOM_CUTOFFS[paletteOrMetric] && palette.length === 5) {
    const customInternal = METRIC_CUSTOM_CUTOFFS[paletteOrMetric];
    const cutoffs = [min, ...customInternal, max];

    const bins: ColorBin[] = [];
    for (let i = 0; i < 5; i++) {
      const binMin = cutoffs[i];
      const binMax = cutoffs[i + 1];

      const label = unit
        ? `${formatMetricValue(binMin, unit)} – ${formatMetricValue(binMax, unit)}`
        : `${binMin.toFixed(1)} – ${binMax.toFixed(1)}`;

      bins.push({
        min: binMin,
        max: binMax,
        color: palette[i],
        label,
      });
    }
    return bins;
  }

  const makeBins = (cutoffs: number[]): ColorBin[] =>
    cutoffs.slice(1).map((binMax, index) => ({
      min: cutoffs[index],
      max: binMax,
      color: palette[index],
      label: unit
        ? `${formatMetricValue(cutoffs[index], unit)} – ${formatMetricValue(binMax, unit)}`
        : `${cutoffs[index].toFixed(1)} – ${binMax.toFixed(1)}`,
    }));

  // 2. Quantile (equal-count percentile) binning — robust to outliers and
  //    guarantees every colour is represented.
  if (rawValues && rawValues.length >= 5 && palette.length === 5) {
    const sorted = [...rawValues].toSorted((a, b) => a - b);
    const n = sorted.length;
    const cutoffs = [
      min,
      sorted[Math.floor(n * 0.2)],
      sorted[Math.floor(n * 0.4)],
      sorted[Math.floor(n * 0.6)],
      sorted[Math.floor(n * 0.8)],
      max,
    ];

    // Special attention for tight ranges: if the percentiles aren't strictly
    // increasing (common when values cluster — e.g. 4G coverage 85-99%), the
    // quantile bands collapse to zero width and the map goes a single colour.
    // Fall back to equal-width across the observed range in that case.
    const strictlyIncreasing = cutoffs.every((c, i) => i === 0 || cutoffs[i - 1] < c);
    if (strictlyIncreasing) {
      return makeBins(cutoffs);
    }
  }

  // 3. Equal-width linear fallback across [min, max]
  const stepSize = (max - min) / palette.length;
  return palette.map((color, index) => ({
    min: min + index * stepSize,
    max: index === palette.length - 1 ? max : min + (index + 1) * stepSize,
    color,
    label: unit
      ? `${formatMetricValue(min + index * stepSize, unit)} – ${formatMetricValue(index === palette.length - 1 ? max : min + (index + 1) * stepSize, unit)}`
      : `${(min + index * stepSize).toFixed(1)} – ${(index === palette.length - 1 ? max : min + (index + 1) * stepSize).toFixed(1)}`,
  }));
}

export { computeColorBins as generateColorBins };

import type { ExpressionSpecification } from "maplibre-gl";

export function createChoroplethExpression(bins: ColorBin[]): ExpressionSpecification {
  if (bins.length === 0) return ["literal", "#cbd5e1"];

  const stepExpression: ExpressionSpecification = ["step", ["coalesce", ["get", "value"], -99_999_999], "#cbd5e1"];

  let prevStop = -Infinity;
  for (const bin of bins) {
    const stop = bin.min;
    if (stop > prevStop) {
      stepExpression.push(stop, bin.color);
      prevStop = stop;
    }
  }

  return stepExpression;
}

export function formatZScore(value: number | null, mean: number, stdDev: number): string {
  if (value === null || stdDev === 0) return "N/A";
  const z = (value - mean) / stdDev;
  const sign = z >= 0 ? "+" : "";
  return `${sign}${z.toFixed(2)}σ`;
}

export function formatZScoreInterpretation(value: number | null, mean: number, stdDev: number, unit: string): string {
  if (value === null || !Number.isFinite(value) || stdDev === 0) return "";
  const diff = value - mean;
  const absDiffFormatted = formatMetricValue(Math.abs(diff), unit);
  const meanFormatted = formatMetricValue(mean, unit);
  if (diff >= 0) {
    return `+${absDiffFormatted} above mean (${meanFormatted})`;
  }
  return `-${absDiffFormatted} below mean (${meanFormatted})`;
}

/**
 * Formats a signed change/delta value with an explicit "+"/"-" prefix and a
 * unit-appropriate suffix (e.g. "+£2,400", "-1.8pp", "+0.4"). Used by the
 * change map so deltas read clearly next to absolute values.
 */
export function formatChangeValue(value: number | null, unit: string): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "Suppressed / Unavailable";
  }
  const sign = value >= 0 ? "+" : "-";
  const absVal = Math.abs(value);

  if (unit === "currency-gbp") {
    const useDecimals = absVal < 1000 && absVal % 1 !== 0;
    return useDecimals ? `${sign}${gbpFormatterDec.format(absVal)}` : `${sign}${gbpFormatterInt.format(absVal)}`;
  }

  if (unit === "percentage") {
    return `${sign}${absVal.toFixed(2)}pp`;
  }

  if (unit === "score") {
    return `${sign}${absVal.toFixed(1)}`;
  }

  if (unit === "ratio") {
    return `${sign}${absVal.toFixed(2)}x`;
  }

  if (unit === "rate") {
    return `${sign}${absVal.toFixed(1)}`;
  }

  const useDecimals = absVal < 1000 && absVal % 1 !== 0;
  return useDecimals ? `${sign}${numFormatterDec.format(absVal)}` : `${sign}${numFormatterInt.format(absVal)}`;
}
